import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaSearch, FaCheck, FaInfoCircle } from 'react-icons/fa';
import { knowledgeBasedCategories } from '../data/knowledgeBasedCategories';

const CategoryModal = ({ isOpen, onClose, onSelect, currentSelection }) => {
  const [selectedMain, setSelectedMain] = useState(currentSelection?.main || '');
  const [selectedTech, setSelectedTech] = useState(currentSelection?.tech || '');
  const [searchTerm, setSearchTerm] = useState('');

  const handleMainSelect = (categoryId) => {
    setSelectedMain(categoryId);
    setSelectedTech(''); // Reset tech selection
  };

  const handleTechSelect = (techId, techName) => {
    setSelectedTech(techId);
  };

  const handleConfirm = () => {
    if (selectedMain && selectedTech) {
      const mainCategory = knowledgeBasedCategories.find(c => c.id === selectedMain);
      const technology = mainCategory?.technologies.find(t => t.id === selectedTech);
      
      onSelect({
        main: selectedMain,
        mainName: mainCategory?.name,
        tech: selectedTech,
        techName: technology?.name
      });
      onClose();
    }
  };

  const filteredCategories = knowledgeBasedCategories.filter(cat =>
    searchTerm === '' || cat.name.includes(searchTerm)
  );

  const selectedCategory = knowledgeBasedCategories.find(c => c.id === selectedMain);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal - بهینه‌سازی شده برای موبایل */}
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0, y: '100%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative w-full max-w-6xl bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] sm:max-h-[85vh] flex flex-col"
            >
              {/* Header - بهینه شده برای موبایل */}
              <div className="flex-shrink-0 bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800 px-4 sm:px-6 py-4 sm:py-5">
                {/* Mobile Handle */}
                <div className="sm:hidden w-12 h-1.5 bg-white/30 rounded-full mx-auto mb-3"></div>
                
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-base sm:text-xl md:text-2xl font-bold text-white leading-tight">انتخاب حوزه دانش‌بنیان</h2>
                  <button
                    onClick={onClose}
                    className="text-white/80 hover:text-white transition-colors p-2 flex-shrink-0 hover:bg-white/10 rounded-lg"
                  >
                    <FaTimes className="text-lg sm:text-xl" />
                  </button>
                </div>
                
                {/* Search - بهبود یافته */}
                <div className="mt-3 sm:mt-4 relative">
                  <input
                    type="text"
                    placeholder="جستجو در حوزه‌ها..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2.5 sm:py-3 pr-10 rounded-lg sm:rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 text-sm sm:text-base transition-all"
                  />
                  <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 text-xs sm:text-sm" />
                </div>
              </div>

              {/* Content - بهینه‌سازی برای موبایل */}
              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6 p-3 sm:p-4 md:p-6">
                  {/* Main Categories */}
                  <div className="order-1">
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-800 dark:text-gray-200 mb-2 sm:mb-3 flex items-center gap-2 sticky top-0 bg-white dark:bg-gray-800 py-2 z-10">
                      <div className="w-1 sm:w-1.5 h-4 sm:h-5 bg-primary-600 rounded"></div>
                      <span>دسته اصلی</span>
                    </h3>
                    <div className="space-y-2">
                      {filteredCategories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => handleMainSelect(category.id)}
                          className={`w-full text-right px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl transition-all duration-200 ${
                            selectedMain === category.id
                              ? 'bg-primary-100 dark:bg-primary-900/30 border-2 border-primary-600 text-primary-800 dark:text-primary-400 shadow-md'
                              : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 border-2 border-transparent active:scale-98'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 sm:gap-3">
                            <span className="font-medium text-xs sm:text-sm md:text-base leading-relaxed text-right">{category.name}</span>
                            {selectedMain === category.id && <FaCheck className="text-primary-600 dark:text-primary-400 text-base sm:text-lg flex-shrink-0" />}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Technologies */}
                  <div className="order-2">
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-800 dark:text-gray-200 mb-2 sm:mb-3 flex items-center gap-2 sticky top-0 bg-white dark:bg-gray-800 py-2 z-10">
                      <div className="w-1 sm:w-1.5 h-4 sm:h-5 bg-secondary-500 rounded"></div>
                      <span>دسته فناوری</span>
                    </h3>
                    {selectedCategory ? (
                      <div className="space-y-2">
                        {selectedCategory.technologies.map((tech) => (
                          <button
                            key={tech.id}
                            onClick={() => handleTechSelect(tech.id, tech.name)}
                            className={`w-full text-right px-3 sm:px-4 py-3 sm:py-4 rounded-lg sm:rounded-xl transition-all duration-200 ${
                              selectedTech === tech.id
                                ? 'bg-secondary-100 dark:bg-secondary-900/30 border-2 border-secondary-600 text-secondary-800 dark:text-secondary-400 shadow-md'
                                : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 border-2 border-transparent active:scale-98'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2 sm:gap-3">
                              <div className="flex-1 text-right">
                                <div className="font-medium text-xs sm:text-sm md:text-base mb-1 leading-relaxed">{tech.name}</div>
                                <div className="text-[10px] sm:text-xs opacity-70 leading-relaxed">{tech.description}</div>
                              </div>
                              {selectedTech === tech.id && <FaCheck className="text-secondary-600 dark:text-secondary-400 flex-shrink-0 mt-0.5 sm:mt-1 text-base sm:text-lg" />}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 sm:py-12 md:py-16 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg sm:rounded-xl">
                        <FaInfoCircle className="text-2xl sm:text-3xl md:text-4xl mx-auto mb-2 sm:mb-3 opacity-50" />
                        <p className="text-xs sm:text-sm md:text-base font-medium px-4">ابتدا یک دسته اصلی انتخاب کنید</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer - بهینه‌سازی برای موبایل */}
              <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 px-3 sm:px-4 md:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-900">
                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-3">
                  <button
                    onClick={onClose}
                    className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:scale-98 transition-all font-medium text-sm sm:text-base"
                  >
                    انصراف
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={!selectedMain || !selectedTech}
                    className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 text-white font-bold hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed active:scale-98 transition-all shadow-lg disabled:shadow-none text-sm sm:text-base"
                  >
                    تایید انتخاب
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CategoryModal;
